import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { api } from "../lib/api";

type AuthState = {
  authenticated: boolean;
  /** true, solange die bestehende Sitzung noch geprüft wird. */
  checking: boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  // Beim Laden prüfen, ob noch ein gültiges Session-Cookie existiert.
  useEffect(() => {
    let cancelled = false;

    api.api.auth.me
      .$get()
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setAuthenticated(data.authenticated);
      })
      .catch(() => {
        if (!cancelled) setAuthenticated(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (password: string) => {
    const res = await api.api.auth.login.$post({ json: { password } });
    if (!res.ok) {
      throw new Error(res.status === 401 ? "Falsches Passwort" : "Anmeldung fehlgeschlagen");
    }
    setAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    await api.api.auth.logout.$post();
    setAuthenticated(false);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ authenticated, checking, login, logout }),
    [authenticated, checking, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth muss innerhalb von <AuthProvider> verwendet werden");
  }
  return context;
}
